import {
  AnimatedFormError,
  Badge,
  Box,
  ComboBox,
  ComboBoxContent,
  ComboBoxInput,
  ComboBoxItem,
  Text,
} from "@prismicio/editor-ui"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useRequestRepository } from "../../../hooks/useRequestRepository"

// Available API paths, filters and orderings may only support a subset of these.
export const apiPaths = {
  doc: "document",
  docType: "document.type",
  docTags: "document.tags",
  docFirstPublicationDate: "document.first_publication_date",
  docLastPublicationDate: "document.last_publication_date",
  docId: "document.id",
  myDocUid: "my.[document-type].uid",
  myDocField: "my.[document-type].[field]",
}

type RequestAPIPathFieldsetProps = {
  path: string
  setPath: (path: string) => void
  description: string
  allowedPaths: string[]
  context: "filter" | "ordering"
}

export function RequestAPIPathFieldset(props: RequestAPIPathFieldsetProps) {
  const { path, setPath, description, allowedPaths, context } = props

  // We use a local state as a proxy to only set a new path when it's valid.
  const [search, _setSearch] = useState(path)
  const searchRef = useRef<HTMLInputElement>(null)
  const customTypeIds = Object.keys(useRequestRepository()?.types ?? {})

  const setSearch = useCallback(
    (value: string) => {
      // When user select a path that ends with "[field]", we want them to finish their selection by typing the field API ID, so we focus the input.
      if (value.endsWith("[field]")) {
        value = value.replace("[field]", "")
        // Focus on the next frame to ensure the ref is available.
        requestAnimationFrame(() => searchRef.current?.focus())
      }

      _setSearch(value)
      if (isValidAPIPath(value, { allowedPaths, customTypeIds, context })) {
        setPath(value)
      } else {
        setPath("")
      }
    },
    [setPath, allowedPaths, customTypeIds, context],
  )

  // Keep the search value in sync with the path.
  useEffect(() => {
    if (isValidAPIPath(path, { allowedPaths, customTypeIds, context })) {
      _setSearch(path)
    }
  }, [path, allowedPaths, customTypeIds, context])

  const error = useMemo(() => {
    try {
      validateAPIPath(search, { allowedPaths, customTypeIds, context })
      return
    } catch (error) {
      return error instanceof Error ? error.message : "Invalid path."
    }
  }, [search, allowedPaths, customTypeIds, context])

  // Paths that are currently available to be used.
  const availablePaths = allowedPaths.filter(
    (allowedPath) =>
      ![apiPaths.myDocUid, apiPaths.myDocField].includes(allowedPath),
  )

  if (customTypeIds.length) {
    for (const customTypeId of customTypeIds) {
      if (allowedPaths.includes(apiPaths.myDocUid)) {
        availablePaths.push(`my.${customTypeId}.uid`)
      }
      if (allowedPaths.includes(apiPaths.myDocField)) {
        availablePaths.push(`my.${customTypeId}.[field]`)
      }
    }
  } else {
    if (allowedPaths.includes(apiPaths.myDocUid)) {
      availablePaths.push(apiPaths.myDocUid)
    }
    if (allowedPaths.includes(apiPaths.myDocField)) {
      availablePaths.push(apiPaths.myDocField)
    }
  }

  // Suggests paths based on search value and custom type IDs.
  let suggestedPaths = availablePaths.filter((allowedPath) =>
    allowedPath.replace("[field]", "").includes(search.toLowerCase()),
  )
  if (suggestedPaths.length === 1 && suggestedPaths[0] === search) {
    suggestedPaths = []
  }

  // We favor the my.[document-type].* path as a placeholder when available, as it's the most common path.
  const placeholder = allowedPaths.includes(apiPaths.myDocField)
    ? apiPaths.myDocField
    : allowedPaths[0]

  return (
    <Box display="grid">
      <Text variant="emphasized" component="label" htmlFor="api-path">
        Path
      </Text>
      <Text component="span" color="grey11" sx={{ marginBottom: 4 }}>
        {description}
      </Text>
      <ComboBox variant="detached-connected">
        <ComboBoxInput
          id="api-path"
          value={search}
          onValueChange={setSearch}
          placeholder={placeholder}
          startAdornment={false}
          ref={searchRef}
        />
        {suggestedPaths.length > 0 && (
          <ComboBoxContent>
            {suggestedPaths.map((filteredPath) => (
              <ComboBoxItem
                key={filteredPath}
                value={filteredPath}
                onCheckedChange={() => setSearch(filteredPath)}
                disabled={filteredPath.replace("[field]", "") === search}
              >
                <Box padding={4} alignItems="center">
                  <Badge title={filteredPath} color="purple" size="medium" />
                  {filteredPath.replace("[field]", "") === search && (
                    <Text variant="small" color="grey11">
                      (complete with the desired field API ID)
                    </Text>
                  )}
                </Box>
              </ComboBoxItem>
            ))}
          </ComboBoxContent>
        )}
      </ComboBox>
      <AnimatedFormError error={error} />
    </Box>
  )
}

interface ValidateAPIPathOptions {
  allowedPaths: string[]
  customTypeIds?: string[]
  context: "filter" | "ordering"
}

export function validateAPIPath(
  path: string | undefined,
  options: ValidateAPIPathOptions,
): asserts path is string {
  const { allowedPaths, customTypeIds } = options

  const errorContext =
    options.context === "filter" ? "by this filter" : "with orderings"

  if (!path) {
    throw new Error("Path is required.")
  }

  // Generic document paths
  if (path.startsWith("document")) {
    if (!Object.values(apiPaths).includes(path)) {
      throw new Error(`Invalid \`document.*\` path.`)
    }

    if (!allowedPaths.includes(path)) {
      throw new Error(`\`${path}\` paths are not allowed ${errorContext}.`)
    }

    return
  }

  // Specific (my.[document-type].*) document paths
  const [my, type, field, ...rest] = path.split(".")
  if (my === "my" && type && rest.length === 0) {
    if (field === "uid") {
      // my.[document-type].uid special case
      if (!allowedPaths.includes(apiPaths.myDocUid)) {
        throw new Error(
          `\`${apiPaths.myDocUid}\` paths are not allowed ${errorContext}.`,
        )
      }

      if (path.includes("[document-type]")) {
        throw new Error(
          "`[document-type]` is a placeholder and should be replaced with a valid document type (e.g. `my.blog_post.uid`).",
        )
      }

      if (customTypeIds?.length && !customTypeIds.includes(type)) {
        throw new Error(
          `\`${type}\` is not a valid document type on this repository.`,
        )
      }

      return
    } else {
      // my.[document-type].[field] case
      if (!allowedPaths.includes(apiPaths.myDocField)) {
        throw new Error(
          `\`${apiPaths.myDocField}\` paths are not allowed ${errorContext}.`,
        )
      }

      if (type === "[document-type]") {
        throw new Error(
          "`[document-type]` is a placeholder and should be replaced with a valid document type (e.g. `my.blog_post.title`).",
        )
      }

      if (customTypeIds?.length && !customTypeIds.includes(type)) {
        throw new Error(
          `\`${type}\` is not a valid document type on this repository.`,
        )
      }

      if (field === "[field]") {
        throw new Error(
          "`[field]` is a placeholder and should be replaced with a valid field API ID.",
        )
      }

      if (!field) {
        throw new Error("Field API ID is missing.")
      }

      return
    }
  }

  throw new Error(
    "Invalid path format. Valid paths must start with `document.` or `my.`.",
  )
}

function isValidAPIPath(
  path: string,
  options: ValidateAPIPathOptions,
): boolean {
  try {
    validateAPIPath(path, options)
    return true
  } catch (error) {
    return false
  }
}
